import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, Input, FormGroup, Label } from 'reactstrap';
import TextField from '@mui/material/TextField';

const DashboardDropdown = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { workspaceId } = useParams();
  const { dashboardList, selectedDashboardId, dashboardListLoading, isSaving, templates = [], templatesLoading } = useSelector(state => state.Analytics);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', selectedTemplates: [] });
  const [formError, setFormError] = useState('');
  const searchInputRef = useRef(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);

  // Get selected dashboard
  const selectedDashboard = dashboardList.find(d => d._id === selectedDashboardId);

  // Filter dashboards by search
  const filteredDashboards = dashboardList.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle dashboard selection - dispatch action and update URL
  const handleSelect = (dashboardId) => {
    dispatch({ type: 'SELECT_DASHBOARD', payload: dashboardId });
    // Update URL with selected dashboard ID
    const basePath = workspaceId ? `/${workspaceId}/dashboard` : '/dashboard';
    history.push(`${basePath}/${dashboardId}`);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle set default
  const handleSetDefault = (e, dashboardId) => {
    e.stopPropagation();
    dispatch({ type: 'SET_DEFAULT_DASHBOARD', payload: { dashboardId } });
  };

  // Handle edit dashboard (from dropdown action item)
  const handleEditDashboard = () => {
    if (selectedDashboard) {
      setEditingDashboard(selectedDashboard);
      setFormData({ name: selectedDashboard.name, description: selectedDashboard.description || '' });
      setModalMode('edit');
      setModalOpen(true);
      setIsOpen(false);
    }
  };

  // Handle create new
  const handleCreateNew = () => {
    setEditingDashboard(null);
    setFormData({ name: '', description: '', selectedTemplates: [] });
    setModalMode('create');
    setModalOpen(true);
    setIsOpen(false);
    // Fetch templates when opening create modal
    dispatch({ type: 'GET_TEMPLATES' });
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingDashboard(null);
    setFormData({ name: '', description: '', selectedTemplates: [] });
    setFormError('');
  };

  // Handle template selection (toggle)
  const handleTemplateToggle = (templateId) => {
    setFormData(prev => {
      const isSelected = prev.selectedTemplates.includes(templateId);
      return {
        ...prev,
        selectedTemplates: isSelected
          ? prev.selectedTemplates.filter(id => id !== templateId)
          : [...prev.selectedTemplates, templateId]
      };
    });
  };

  // Handle "Start from blank" selection
  const handleBlankSelect = () => {
    setFormData(prev => ({
      ...prev,
      selectedTemplates: [] // Clear all template selections
    }));
  };

  // Handle form submit
  const handleSubmit = () => {
    if (!formData.name.trim()) {
      setFormError('Dashboard name is required');
      return;
    }

    if (modalMode === 'create') {
      // If templates are selected, create from template(s)
      if (formData.selectedTemplates.length > 0) {
        dispatch({
          type: 'CREATE_DASHBOARD_FROM_TEMPLATE',
          payload: {
            templateIds: formData.selectedTemplates,
            name: formData.name.trim(),
            description: formData.description.trim()
          }
        });
      } else {
        // Create blank dashboard
        dispatch({ type: 'CREATE_DASHBOARD', payload: { name: formData.name.trim(), description: formData.description.trim() } });
      }
    } else {
      dispatch({ type: 'UPDATE_DASHBOARD_DETAILS', payload: { dashboardId: editingDashboard._id, name: formData.name.trim(), description: formData.description.trim() } });
    }
    handleModalClose();
  };

  // Handle delete dashboard - open confirmation modal
  const handleDelete = () => {
    if (editingDashboard) {
      setDashboardToDelete(editingDashboard);
      setDeleteModalOpen(true);
      handleModalClose();
    }
  };

  // Confirm delete dashboard
  const confirmDeleteDashboard = () => {
    if (dashboardToDelete) {
      dispatch({ type: 'DELETE_DASHBOARD', payload: { dashboardId: dashboardToDelete._id } });
    }
    setDeleteModalOpen(false);
    setDashboardToDelete(null);
  };

  // Cancel delete dashboard
  const cancelDeleteDashboard = () => {
    setDeleteModalOpen(false);
    setDashboardToDelete(null);
  };

  return (
    <>
      <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
        <DropdownToggle caret>
          {dashboardListLoading ? 'Loading...' : (selectedDashboard?.name || 'Select Dashboard')}
          <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.99316 2.46804L1.91042 0.544434L0.307735 0.544434C0.0334783 0.544434 -0.10365 0.986291 0.090615 1.24531L1.57046 3.21843C1.80758 3.53459 2.19325 3.53459 2.43037 3.21843L2.99316 2.46804Z" fill="var(--primary-color)" />
            <path d="M3.69092 0.544434L1.91111 0.544434L2.99386 2.46804L3.9109 1.24531C4.10231 0.986291 3.96518 0.544434 3.69092 0.544434Z" fill="var(--primary-color)" />
          </svg>
        </DropdownToggle>
        <DropdownMenu>
          {/* Search Input */}
          <div className="dropdown-search" onClick={(e) => e.stopPropagation()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search dashboards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Dashboard List */}
          <div className="dashboard-list">
            {filteredDashboards.length === 0 ? (
              <div className="no-results">No dashboards found</div>
            ) : (
              filteredDashboards.map(dashboard => (
                <div
                  key={dashboard._id}
                  className={`dashboard-item${dashboard._id === selectedDashboardId ? ' selected' : ''}`}
                  onClick={() => handleSelect(dashboard._id)}
                >
                  <div className="dashboard-item-info">
                    <span className="dashboard-item-name">{dashboard.name}</span>
                    {dashboard.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                  </div>
                  {!dashboard.isDefault && (
                    <button
                      className="set-default-btn"
                      onClick={(e) => handleSetDefault(e, dashboard._id)}
                      title="Set as default"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Action Items - matching Deals page style */}
          <DropdownItem divider />
          <DropdownItem onClick={handleEditDashboard}>
            <div className='action-item'>
              <i className='icon'>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </i>
              Edit Dashboard
            </div>
          </DropdownItem>
          <DropdownItem onClick={handleCreateNew}>
            <div className='action-item'>
              <i className='icon'>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </i>
              Add Dashboard
            </div>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* Create/Edit Dashboard Modal */}
      <Modal isOpen={modalOpen} toggle={handleModalClose} className={`main-modal new-user-modal modal-dialog-centered ${modalMode === 'create' ? 'select-tampltes-modal' : 'bulkedit-modal'}`}>
        <ModalHeader toggle={handleModalClose}></ModalHeader>
        <ModalBody>
          {modalMode === 'create' ? (
            /* Template Selection Mode */
            <div className='add-content'>
              <h2>Select Template</h2>
              <div className='templates-inner'>
                <div className='account-tabs'>
                  <div className='searchbox'>
                    <label>Dashboard Name</label>
                    <TextField
                      fullWidth
                      hiddenLabel
                      name='name'
                      size='small'
                      variant='standard'
                      placeholder='Dashboard Name'
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setFormError('');
                      }}
                      error={!!formError}
                      helperText={formError}
                    />
                  </div>
                  <div className='searchbox' style={{ marginTop: '10px' }}>
                    <label>Description (optional)</label>
                    <TextField
                      fullWidth
                      hiddenLabel
                      name='description'
                      size='small'
                      variant='standard'
                      placeholder='Dashboard Description'
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className='account-tab-inner'>
                    <div className='right-tabs-area full-width'>
                      <div className='tabs-content'>
                        <div className='template-tabs'>
                          <div className='template-inner'>
                            {/* Start from Blank Option */}
                            <div
                              className={`template-box style-full ${formData.selectedTemplates.length === 0 ? 'active' : ''}`}
                              onClick={handleBlankSelect}
                              style={{ marginTop: '15px' }}
                            >
                              <button className='btn-add'>
                                <div className='icon-area'>
                                  <i>
                                    <svg width="59" height="59" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M29.5 12.292V46.7087" stroke="#4b5563" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"></path>
                                      <path d="M12.2917 29.5H46.7083" stroke="#4b5563" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>
                                  </i>
                                  <h5>Start from blank</h5>
                                </div>
                                <div className='text-box'>
                                  <p>A blank dashboard with no pre-defined widgets.</p>
                                </div>
                              </button>
                            </div>

                            {/* Template Cards */}
                            {templatesLoading ? (
                              <div className="template-box" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>Loading templates...</p>
                              </div>
                            ) : (
                              (templates || []).map(template => (
                                <div
                                  key={template._id}
                                  className={`template-box ${formData.selectedTemplates.includes(template._id) ? 'active' : ''}`}
                                  onClick={() => handleTemplateToggle(template._id)}
                                >
                                  <button className='btn-add'>
                                    <div className='icon-area'>
                                      <h5>{template.name}</h5>
                                    </div>
                                    <div className='text-box'>
                                      <p>{template.description}</p>
                                    </div>
                                  </button>
                                </div>
                              ))
                            )}

                            {/* Action Buttons */}
                            <div className='btn-box'>
                              <button className='btn-style-border' onClick={handleModalClose}>Close</button>
                              <button className='btn-style-new' onClick={handleSubmit} disabled={isSaving}>
                                {isSaving ? 'Creating...' : 'Create Dashboard'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <div className='content-area'>
              <div className='delete-modal dashboard-modal-content'>
                <h4>
                  <i className='icon'>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='var(--primary-color)' fillOpacity='0.15' />
                      <path d='M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13' stroke='var(--primary-color)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' transform='translate(2, 1) scale(0.85)'/>
                      <path d='M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z' stroke='var(--primary-color)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' transform='translate(2, 1) scale(0.85)'/>
                    </svg>
                  </i>
                  Edit Dashboard
                </h4>
                <div className='form-area'>
                  <FormGroup className="group-form">
                    <Label for="dashboardName">Name <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      id="dashboardName"
                      placeholder="Enter dashboard name"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setFormError('');
                      }}
                      invalid={!!formError}
                    />
                    {formError && <div className="invalid-feedback">{formError}</div>}
                  </FormGroup>
                  <FormGroup className="group-form">
                    <Label for="dashboardDescription">Description</Label>
                    <Input
                      type="textarea"
                      id="dashboardDescription"
                      placeholder="Enter dashboard description (optional)"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                    />
                  </FormGroup>
                  <div className='btns-box text-right'>
                    {!editingDashboard?.isDefault && (
                      <button className="btn-style-border btn-danger-outline" onClick={handleDelete} style={{ marginRight: 'auto' }}>
                        Delete
                      </button>
                    )}
                    <button className='btn-style-border' onClick={handleModalClose}>Cancel</button>
                    <button className='btn-style-new' onClick={handleSubmit} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Delete Dashboard Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={cancelDeleteDashboard} className="main-modal modal-dialog-centered new-user-modal bulkedit-modal">
        <ModalHeader toggle={cancelDeleteDashboard}></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fillOpacity='0.15' />
                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                </i>
                Delete Dashboard
              </h4>
              <p>Are you sure you want to delete <b>{dashboardToDelete?.name}</b>? You can't undo this action afterwards.</p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={cancelDeleteDashboard}>Cancel</button>
                  <button className='btn-style-new' onClick={confirmDeleteDashboard}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DashboardDropdown;
